(ns manage.adset-decision-tree-page.sub
  (:require [goog.object :as gobject]
            [manage.adset-decision-tree-page.editor.util
             :refer [get-node-ascendants get-outcome-descendants]]
            [manage.adset-decision-tree-page.rotation :as rotation]
            [manage.adset-decision-tree-page.selection :as selection]
            [mate.core :as mc]
            [re-frame.core :as rf]))

(rf/reg-sub ::editor
  (fn [db _]
    (:decision-tree-editor db)))

(rf/reg-sub ::editor-is-initialized
  (fn [db _]
    (contains? db :decision-tree-editor)))

(rf/reg-sub ::editor-read-only
  :<- [::editor]
  (fn [editor]
    (:read-only editor)))

(rf/reg-sub ::editor-auto-layout
  :<- [::editor]
  (fn [editor]
    (:auto-layout editor)))

(rf/reg-sub ::decision-tree-id
  :<- [::editor]
  (fn [editor _]
    (:decision-tree-id editor)))

(rf/reg-sub ::reactflow-nodes-map
  :<- [::editor]
  (fn [editor _]
    (:reactflow-nodes editor)))

(rf/reg-sub ::reactflow-nodes
  :<- [::reactflow-nodes-map]
  (fn [reactflow-nodes-map _]
    (to-array (vals reactflow-nodes-map))))

;; This is temporary, the hidden status should be read from the CLJS data instead, as the source of truth.
(rf/reg-sub ::reactflow-node-is-hidden
  :<- [::reactflow-nodes-map]
  (fn [reactflow-nodes-map [_ reactflow-node-id]]
    (some-> (get reactflow-nodes-map reactflow-node-id)
            (gobject/get "hidden" false))))

(rf/reg-sub ::reactflow-edges
  :<- [::editor]
  (fn [editor]
    (vals (:reactflow-edges editor))))

(rf/reg-sub ::reactflow-edges-for-render
  :<- [::reactflow-edges]
  (fn [reactflow-edges [_ {:keys [edge-fn] :or {edge-fn identity}}]]
    (->> reactflow-edges (map edge-fn) to-array)))

(rf/reg-sub ::node-id->node-data
  :<- [::editor]
  (fn [editor _]
    (:node-id->node-data editor)))

(rf/reg-sub ::node-id->children
  :<- [::node-id->node-data]
  (fn [node-id->node-data]
    (->> (vals node-id->node-data)
         (group-by :parent-id))))

(rf/reg-sub ::children-by-node-id
  :<- [::node-id->children]
  (fn [node-id->children [_ node-id]]
    (get node-id->children node-id)))

(rf/reg-sub ::node-data
  :<- [::node-id->node-data]
  (fn [node-id->node-data [_ node-id]]
    (get node-id->node-data node-id)))

(rf/reg-sub ::node-type
  (fn [[_ node-id]]
    (rf/subscribe [::node-data node-id]))
  (fn [node-data _]
    (-> node-data :type)))

(rf/reg-sub ::selection
  :<- [::editor]
  (fn [editor _]
    (:selection editor [])))

(rf/reg-sub ::selected-outcome-node-ids
  :<- [::selection]
  (fn [selection _]
    (into #{}
          (keep (fn [{:keys [type outcome-node-id]}]
                  (when (= type :selection/outcome-node)
                    outcome-node-id)))
          selection)))

(rf/reg-sub ::outcome-selected?
  :<- [::selected-outcome-node-ids]
  (fn [selected-outcome-node-ids [_ node-id]]
    (contains? selected-outcome-node-ids node-id)))

(def selectable-item-types #{:selection/decision-node
                             :selection/outcome-node
                             :selection/variant
                             :selection/product-selector
                             :selection/default-outcome-node})

(rf/reg-sub ::first-selected-item
  :<- [::selection]
  (fn [selection _]
    (some (fn [selected-item]
            (when (contains? selectable-item-types (:type selected-item))
              selected-item))
          selection)))

(rf/reg-sub ::is-item-selected
  :<- [::first-selected-item]
  (fn [current-selected-item [_ selected-item]]
    (= current-selected-item selected-item)))

(rf/reg-sub ::selected-node
  (fn [[_ selected-item]]
    (let [host-node-id (selection/get-host-node-id selected-item)]
      (rf/subscribe [::node-data host-node-id])))
  (fn [node-data [_ _selected-item]]
    node-data))

(rf/reg-sub ::new-node-selected
  :<- [::editor]
  (fn [editor _]
    (:new-node-selected editor)))

(rf/reg-sub ::selection-path
  :<- [::node-id->node-data]
  :<- [::selection]
  (fn [[node-id->node-data [first-selection :as selection]] _]
    (when (= (count selection) 1)
      (-> node-id->node-data
          (get-node-ascendants (selection/get-host-node-id first-selection))
          (assoc :selected-item first-selection)))))

(rf/reg-sub ::highlighted-edge-ids
  :<- [::selection-path]
  (fn [selected-node-ascendants _]
    (set (:edge-ids selected-node-ascendants))))

(rf/reg-sub ::highlighted-outcome-node-ids
  :<- [::selection-path]
  (fn [selected-node-ascendants _]
    (set (:outcome-node-ids selected-node-ascendants))))

(rf/reg-sub ::outcome-highlighted?
  :<- [::highlighted-outcome-node-ids]
  (fn [highlighted-outcome-node-ids [_ node-id]]
    (contains? highlighted-outcome-node-ids node-id)))

(rf/reg-sub ::selected-outcome-descendants
  :<- [::selection]
  :<- [::node-id->children]
  (fn [[selection node-id->children]]
    (when (= (count selection) 1)
      (let [{:keys [outcome-node-id]} (first selection)]
        (get-outcome-descendants node-id->children
                                 {:id outcome-node-id})))))

(rf/reg-sub ::is-decision-node-dashed
  :<- [::selected-outcome-descendants]
  (fn [selected-outcome-descendants [_ id]]
    (= id (:decision-node-id selected-outcome-descendants))))

(rf/reg-sub ::dashed-edge-id
  :<- [::selected-outcome-descendants]
  (fn [selected-outcome-descendants _]
    (:edge-id selected-outcome-descendants)))

(rf/reg-sub ::decision-tree-saving-status
  :<- [::editor]
  (fn [editor _]
    (cond
      (seq (:nodes-to-patch editor)) :patch-debounced
      (seq (:tracked-mutation-ids editor)) :fetching
      (:communication-error editor) :error
      :else :success)))

(rf/reg-sub ::view-mode
  :<- [:router/query-parameters]
  (fn [{:keys [view-mode]}]
    (or view-mode "table")))

(rf/reg-sub ::root-node-id
  :<- [::editor]
  (fn [{:keys [root-node-id]} _] root-node-id))

(rf/reg-sub ::root-as-selected-item
  :<- [::root-node-id]
  (fn [root-node-id _]
    {:type :selection/decision-node
     :decision-node-id root-node-id}))

(rf/reg-sub ::root-node-data
  :<- [::node-id->node-data]
  :<- [::root-node-id]
  (fn [[node-id->node-data root-node-id] _]
    (get node-id->node-data root-node-id)))

(rf/reg-sub ::root-even-rotation-enabled?
  :<- [::root-node-data]
  (fn [root-node-data _]
    (rotation/root-even-rotation? root-node-data)))

(rf/reg-sub ::root-weighted-distribution-enabled?
  :<- [::root-node-data]
  (fn [root-node-data _]
    (rotation/root-weighted-distribution? root-node-data)))

(rf/reg-sub ::root-weighted-rotation-node
  :<- [::root-node-id]
  :<- [::node-id->node-data]
  (fn [[root-node-id node-id->node-data] _]
    (rotation/find-root-weighted-rotation-node root-node-id node-id->node-data)))

(rf/reg-sub ::rotation-connect-blocked?
  :<- [::node-id->node-data]
  (fn [node-id->node-data [_ decision-node-id]]
    (when-let [node (get node-id->node-data decision-node-id)]
      (and (rotation/variants-rotation? node)
           (rotation/has-path-ad-variants? node node-id->node-data)))))

(rf/reg-sub ::rotation-any-path-connected?
  :<- [::node-id->node-data]
  :<- [::node-id->children]
  (fn [[node-id->node-data node-id->children] [_ decision-node-id]]
    (when-let [node (get node-id->node-data decision-node-id)]
      (rotation/any-path-connected? node node-id->children))))

(def newer-revision-ready-for-update? (comp #{"approved"} :ad-variant-status))
(def get-ready-newer-revisions (partial filter newer-revision-ready-for-update?))
(def has-ready-newer-revisions? (comp seq get-ready-newer-revisions :newer-revisions))
(def get-ad-variants-ready-for-update (partial filter has-ready-newer-revisions?))
(def some-ready-for-update? (comp seq get-ad-variants-ready-for-update))

(rf/reg-sub ::has-newer-revisions
  :<- [::node-id->node-data]
  (fn [node-id->node-data _]
    (->> (vals node-id->node-data)
         (mapcat :ad-variants)
         some-ready-for-update?)))

(defn archived-ad-variant?
  [{:keys [archived newer-revisions]}]
  (boolean (or (and archived (empty? newer-revisions))
               (:archived (first (sort-by :revision > newer-revisions))))))

(def some-archived-variants? (comp seq (partial filter archived-ad-variant?)))

(rf/reg-sub ::has-archived-ad-variants-in-tree
  :<- [::node-id->node-data]
  (fn [node-id->node-data _]
    (->> (vals node-id->node-data)
         (mapcat :ad-variants)
         (some archived-ad-variant?))))

(rf/reg-sub ::ad-variant-search-enabled
  :<- [::editor]
  (fn [editor]
    (get-in editor [:ad-variant-search :enabled])))

(rf/reg-sub ::ad-variant-search-text
  :<- [::editor]
  (fn [editor]
    (get-in editor [:ad-variant-search :text])))

(rf/reg-sub ::immutable-node-id->node-data
  :<- [::editor]
  (fn [{:keys [node-id->be-node node-id->node-data]}]
    (mc/index-by :immutable-id #(get node-id->node-data (:id %)) (vals node-id->be-node))))
